import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PageLoader from './PageLoader';
import { loadGoogleMaps, onGoogleMapsAuthFailure } from '../lib/googleMapsLoader';
import { REGION_MAP_STYLES } from './RegionGoogleMap';

const coordinates = (item) => {
  if (item?.latitude == null || item?.longitude == null || item.latitude === '' || item.longitude === '') return null;
  const lat = Number(item?.latitude);
  const lng = Number(item?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
};

const markerIcon = (maps, active = false, subject = false) => ({
  path: maps.SymbolPath.CIRCLE,
  scale: subject ? 8 : active ? 9 : 6,
  fillColor: subject ? '#1e3a5f' : '#f59e0b',
  fillOpacity: 1,
  strokeColor: active ? '#0f766e' : '#ffffff',
  strokeOpacity: 1,
  strokeWeight: active ? 3 : 1.8,
});

const compId = (comp) => comp?.comp_sale_id || comp?.comp_rental_id;

export default function SoldCompReviewMap({ subject, comps = [], activeCompId, onSelectComp, onSelectSubject, onUnavailable, children, compType = 'sold' }) {
  const mapElementRef = useRef(null);
  const stateRef = useRef(null);
  const markersRef = useRef(new Map());
  const handlersRef = useRef({ onSelectComp, onSelectSubject, onUnavailable });
  const [ready, setReady] = useState(false);
  const [overlayElement, setOverlayElement] = useState(null);
  handlersRef.current = { onSelectComp, onSelectSubject, onUnavailable };

  useEffect(() => {
    let cancelled = false;
    const mountedMarkers = markersRef.current;
    const stopAuthWatch = onGoogleMapsAuthFailure((error) => {
      if (!cancelled) handlersRef.current.onUnavailable?.(error.message);
    }) || (() => {});
    loadGoogleMaps().then((maps) => {
      if (cancelled || !mapElementRef.current) return;
      const map = new maps.Map(mapElementRef.current, {
        backgroundColor: '#f4f7f8', clickableIcons: false, disableDefaultUI: true,
        gestureHandling: 'cooperative', keyboardShortcuts: false, minZoom: 8, maxZoom: 19,
        styles: REGION_MAP_STYLES, zoom: 13, center: coordinates(subject) || { lat: 39.5, lng: -98.35 },
        zoomControl: true, zoomControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM },
      });
      const overlayNode = document.createElement('div');
      overlayNode.className = 'sold-comp-review-map__overlay';
      const overlay = new maps.OverlayView();
      overlay.position = null;
      overlay.onAdd = () => overlay.getPanes().floatPane.appendChild(overlayNode);
      overlay.draw = () => {
        if (!overlay.position) return;
        const projection = overlay.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(overlay.position);
        overlayNode.style.left = `${point.x}px`;
        overlayNode.style.top = `${point.y}px`;
      };
      overlay.onRemove = () => overlayNode.remove();
      overlay.setMap(map);
      stateRef.current = { maps, map, overlay, overlayNode };
      setOverlayElement(overlayNode);
      setReady(true);
    }).catch((error) => {
      if (!cancelled) handlersRef.current.onUnavailable?.(error.message || 'Could not load Google Maps.');
    });
    return () => {
      cancelled = true;
      stopAuthWatch();
      mountedMarkers.forEach((marker) => marker.setMap(null));
      mountedMarkers.clear();
      stateRef.current?.overlay.setMap(null);
      stateRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const state = stateRef.current;
    if (!state) return;
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current.clear();
    const bounds = new state.maps.LatLngBounds();
    const subjectPosition = coordinates(subject);
    if (subjectPosition) {
      bounds.extend(subjectPosition);
      const marker = new state.maps.Marker({
        map: state.map, position: subjectPosition, title: subject.formatted_address || 'Subject property',
        icon: markerIcon(state.maps, false, true), zIndex: 30,
      });
      marker.addListener('click', () => handlersRef.current.onSelectSubject?.());
      markersRef.current.set('subject', marker);
    }
    comps.forEach((comp) => {
      const position = coordinates(comp);
      const id = compId(comp);
      if (!position || !id) return;
      bounds.extend(position);
      const marker = new state.maps.Marker({
        map: state.map, position,
        title: [comp.street_address, comp.city, comp.state_abbr].filter(Boolean).join(', ') || (compType === 'rent' ? 'Rental comp' : 'Sold comp'),
        icon: { ...markerIcon(state.maps), fillColor: compType === 'rent' ? '#2563eb' : '#f59e0b' },
        zIndex: 12,
      });
      marker.addListener('click', () => handlersRef.current.onSelectComp?.(id));
      markersRef.current.set(id, marker);
    });
    if (!bounds.isEmpty?.()) state.map.fitBounds(bounds, { top: 90, right: 390, bottom: 70, left: 70 });
  }, [comps, subject, ready, compType]); // active styling is updated separately to avoid refitting the camera

  useEffect(() => {
    const state = stateRef.current;
    if (!state) return;
    comps.forEach((comp, index) => {
      const marker = markersRef.current.get(compId(comp));
      if (!marker) return;
      const active = compId(comp) === activeCompId || (activeCompId == null && index === 0);
      marker.setIcon({ ...markerIcon(state.maps, active), fillColor: compType === 'rent' ? '#2563eb' : '#f59e0b' });
      marker.setZIndex(active ? 25 : 12);
    });
    const subjectMarker = markersRef.current.get('subject');
    if (subjectMarker) {
      const subjectActive = activeCompId === '__subject__';
      subjectMarker.setIcon(markerIcon(state.maps, subjectActive, true));
      subjectMarker.setZIndex(subjectActive ? 35 : 30);
    }
    const activeComp = comps.find((comp) => compId(comp) === activeCompId) || (activeCompId == null ? comps[0] : null);
    const position = activeCompId === '__subject__' ? coordinates(subject) : coordinates(activeComp);
    state.overlay.position = position ? new state.maps.LatLng(position.lat, position.lng) : null;
    state.overlayNode.style.display = position ? '' : 'none';
    state.overlay.draw();
    if (position) state.map.panTo(position);
  }, [activeCompId, comps, subject, ready, compType]);

  return <div className="sold-comp-review-map" aria-label={`Map of subject property and matching ${compType === 'rent' ? 'rental' : 'sold'} comps`}>
    <div ref={mapElementRef} className="sold-comp-review-map__canvas" />
    {!ready && <div className="sold-comp-review-map__loading"><PageLoader compact label={`Loading ${compType === 'rent' ? 'rental' : 'sold'} comp map`} /></div>}
    {ready && overlayElement ? createPortal(children, overlayElement) : null}
  </div>;
}
